import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // Wait for DOM to be fully loaded and section hydration to complete before injecting mega menu content
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#waitForHydrationAndInject());
    } else {
      // DOM already loaded, but wait for hydration to complete
      requestAnimationFrame(() => this.#waitForHydrationAndInject());
    }
  }

  /**
   * Wait for section hydration to complete before injecting mega menu content
   * This prevents the injected content from being removed when hydration replaces the section
   */
  #waitForHydrationAndInject() {
    // Find the parent section element
    const section = this.closest('.shopify-section');

    if (!section) {
      // No section found, inject immediately
      this.#injectMegaMenuMobileContent();
      return;
    }

    // Check if already hydrated
    if (section.dataset.hydrated === 'true') {
      this.#injectMegaMenuMobileContent();
      return;
    }

    // Watch for hydration to complete using MutationObserver
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-hydrated') {
          if (section.dataset.hydrated === 'true') {
            observer.disconnect();
            // Use setTimeout to ensure the new DOM is fully rendered
            setTimeout(() => this.#injectMegaMenuMobileContent(), 50);
            return;
          }
        }
      }
    });

    observer.observe(section, { attributes: true, attributeFilter: ['data-hydrated'] });

    // Also set a fallback timeout in case hydration doesn't happen (e.g., in design mode)
    setTimeout(() => {
      observer.disconnect();
      // Only inject if not already injected and section still not hydrated
      if (!this.querySelector('.mega-menu-mobile') && section.dataset.hydrated !== 'true') {
        this.#injectMegaMenuMobileContent();
      }
    }, 3000);
  }

  /**
   * Inject mega menu mobile content from source containers into matching menu items
   */
  #injectMegaMenuMobileContent() {
    // Find all mega menu mobile source containers
    const sources = document.querySelectorAll('#mega-menu-mobile-source, [id="mega-menu-mobile-source"]');

    if (sources.length === 0) {
      // Sources might not exist yet, try again later (max 10 attempts)
      this._megaMenuRetryCount = (this._megaMenuRetryCount || 0) + 1;
      if (this._megaMenuRetryCount < 10) {
        setTimeout(() => this.#injectMegaMenuMobileContent(), 200);
      }
      return;
    }

    sources.forEach((source) => {
      const menuItemTitle = source.dataset.menuItem?.toLowerCase()?.trim();
      if (!menuItemTitle) return;

      // Find matching menu item in the drawer (check both summary and a elements)
      const menuItems = this.querySelectorAll('.menu-drawer__menu-item--mainlist');

      menuItems.forEach((menuItem) => {
        const menuItemText = menuItem.querySelector('.menu-drawer__menu-item-text')?.textContent?.trim()?.toLowerCase();
        if (menuItemText !== menuItemTitle) return;

        // Check if mega menu content is already injected
        const parentLi = menuItem.closest('li');
        if (!parentLi || parentLi.querySelector('.mega-menu-mobile')) return;

        // Clone the mega menu content
        const megaMenuContent = source.querySelector('.mega-menu-mobile');
        if (!megaMenuContent) return;

        // Check if this is a summary (accordion) or a link
        const isSummary = menuItem.tagName === 'SUMMARY';
        const isLink = menuItem.tagName === 'A';

        if (isSummary) {
          // Menu item is already an accordion - inject content
          const details = menuItem.closest('details');
          if (details) {
            const contentContainer = details.querySelector('.menu-drawer__menu, .details-content');
            if (contentContainer) {
              // Hide existing content and add mega menu
              const existingContent = contentContainer.children;
              Array.from(existingContent).forEach((child) => {
                if (child instanceof HTMLElement) {
                  child.style.display = 'none';
                }
              });
              contentContainer.insertBefore(megaMenuContent.cloneNode(true), contentContainer.firstChild);
            }
          }
        } else if (isLink) {
          // Menu item is a simple link - convert to accordion
          this.#convertLinkToAccordion(parentLi, menuItem, megaMenuContent);
        }
      });
    });
  }

  /**
   * Convert a simple menu link to an accordion with mega menu content
   * @param {HTMLElement} parentLi - The parent li element
   * @param {HTMLAnchorElement} link - The link element to convert
   * @param {HTMLElement} megaMenuContent - The mega menu content to clone
   */
  #convertLinkToAccordion(parentLi, link, megaMenuContent) {
    const linkText = link.querySelector('.menu-drawer__menu-item-text')?.textContent?.trim() || link.textContent?.trim();
    const linkHandle = linkText.toLowerCase().replace(/\s+/g, '-');

    // Create accordion wrapper
    const accordion = document.createElement('accordion-custom');
    const details = document.createElement('details');
    details.id = `Details-menu-drawer-mega-${linkHandle}`;

    // Create summary
    const summary = document.createElement('summary');
    summary.className = 'menu-drawer__menu-item menu-drawer__menu-item--mainlist menu-drawer__animated-element focus-inset';
    summary.innerHTML = `
      <span class="menu-drawer__menu-item-text wrap-text">${linkText}</span>
      <span class="svg-wrapper icon-caret icon-animated">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    `;

    // Create content container
    const contentDiv = document.createElement('div');
    contentDiv.className = 'menu-drawer__menu details-content';
    contentDiv.appendChild(megaMenuContent.cloneNode(true));

    // Assemble accordion
    details.appendChild(summary);
    details.appendChild(contentDiv);
    accordion.appendChild(details);

    // Hide original link and add accordion
    link.style.display = 'none';
    parentLi.appendChild(accordion);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
